// API服务层 - 封装所有后端API调用
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// 通用API响应类型
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

// 商品相关类型 (从goods API获取)
// 媒体图库项接口
export interface MediaGalleryItem {
  id: number;
  name: string;
  description?: string;
  mediaType: 'IMAGE' | 'VIDEO';
  ossPath: string;
  thumbnailPath?: string;
  width?: number;
  height?: number;
  duration?: number;
  isPrimary?: boolean;
  sortOrder?: number;
}

// Goods 接口（对应后端的 Goods 实体）
export interface Goods {
  id: number;
  name: string;
  description?: string;
  shortDescription?: string;
  salePrice?: number;
  tags?: string;
  status?: string;
  requiresShipping?: boolean;
  stockManagement?: boolean;
  minOrderQuantity?: number;
  maxOrderQuantity?: number;
  viewCount?: number;
  salesCount?: number;
  rating?: number;
  reviewCount?: number;
  createdAt?: string;
  updatedAt?: string;
  deleted?: boolean;
  mediaGalleryList?: MediaGalleryItem[];
}

// Product 类型（与 Goods 类似，保留用于兼容性）
export interface Product {
  id: number;
  name: string;
  description: string;
  price?: number; // 兼容性字段，优先使用salePrice或basePrice
  salePrice?: number; // 销售价格
  basePrice?: number; // 基础价格
  costPrice?: number; // 成本价格
  sku: string;
  stock: number;
  imageUrl?: string;
  category: string;
  active: boolean;
  tags?: string; // 商品标签，后端返回的是逗号分隔的字符串
  createdAt: string;
  updatedAt: string;
  mediaGalleryList?: MediaGalleryItem[]; // 商品关联的媒体图库
}

export interface ProductListResponse {
  success: boolean;
  products: Product[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
}

// 订单相关类型
export interface OrderItem {
  id?: number;
  goods?: Goods;  // 后端返回的是完整的Goods对象
  productName?: string;
  productSku?: string;
  quantity: number;
  unitPrice: number;
  price: number;
  notes?: string;
}

export interface OrderCreateDto {
  items: OrderItem[];
  shippingAddress: string;
  notes?: string;
}

export interface Order {
  id: number;
  orderNumber: string;
  userId: number;
  status: string;
  totalAmount: number;
  totalDiscount?: number;
  appliedCouponIds?: string;
  items?: OrderItem[];
  orderItems?: OrderItem[];
  shippingAddress: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrderListResponse {
  success: boolean;
  orders: Order[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
}

// 配送相关类型
export interface Delivery {
  id: number;
  orderId: number;
  trackingNumber: string;
  carrier: string;
  status: string;
  estimatedDeliveryDate?: string;
  actualDeliveryDate?: string;
  pickupDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// 客服相关类型
export interface SupportConversation {
  id: number;
  userId: number;
  subject: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupportMessage {
  id: number;
  conversationId: number;
  senderId: number;
  senderType: 'USER' | 'ADMIN';
  message: string;
  isRead: boolean;
  createdAt: string;
}

export interface CreateConversationRequest {
  userId: number;
  subject: string;
}

// 支付相关类型
export interface PaymentRequest {
  orderId: number;
  amount: number;
  paymentMethod: string;
  paymentData?: any;
}

// API服务类
class ApiService {
  // 将后端goods数据转换为前端Product格式
  private transformGoodsToProduct(goods: any): Product {
    return {
      id: goods.id,
      name: goods.name,
      description: goods.description,
      price: goods.salePrice || goods.basePrice || 0, // 优先使用salePrice，其次basePrice
      salePrice: goods.salePrice,
      basePrice: goods.basePrice,
      costPrice: goods.costPrice,
      sku: goods.code || goods.sku || '', // 后端可能使用code字段
      stock: goods.stock || 0,
      imageUrl: goods.imageUrl,
      category: goods.category || '',
      active: goods.status === 'ACTIVE',
      tags: goods.tags, // 后端返回的是字符串
      createdAt: goods.createdAt,
      updatedAt: goods.updatedAt,
      mediaGalleryList: this.transformMediaGalleryList(goods.mediaGalleryList || [])
    };
  }

  // 转换媒体图库列表
  private transformMediaGalleryList(mediaGalleryList: any[]): MediaGalleryItem[] {
    return mediaGalleryList.map(media => ({
      id: media.mediaGallery?.id || media.id,
      name: media.mediaGallery?.name || media.name || '',
      description: media.mediaGallery?.description || media.description,
      mediaType: media.mediaGallery?.mediaType || media.mediaType || 'IMAGE',
      ossPath: media.mediaGallery?.ossPath || media.ossPath || '',
      thumbnailPath: media.mediaGallery?.thumbnailPath || media.thumbnailPath,
      width: media.mediaGallery?.width || media.width,
      height: media.mediaGallery?.height || media.height,
      duration: media.mediaGallery?.duration || media.duration,
      isPrimary: media.isPrimary || false,
      sortOrder: media.sortOrder || 0
    }));
  }

  // 批量转换goods数据
  private transformGoodsList(goodsList: any[]): Product[] {
    return goodsList.map(goods => this.transformGoodsToProduct(goods));
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // 获取认证token
    const token = localStorage.getItem('token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();
      
      // 处理401未授权错误（登录过期）
      if (response.status === 401) {
        // 清除本地存储的认证信息
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // 触发认证状态变化事件，通知所有组件更新
        window.dispatchEvent(new Event('auth-changed'));
        
        // 触发登录模态框显示事件
        const event = new CustomEvent('showLoginModal', { 
          detail: { callback: null } 
        });
        window.dispatchEvent(event);
        
        throw new Error(data.message || '登录已过期，请重新登录');
      }
      
      if (!response.ok) {
        throw new Error(data.message || '请求失败');
      }
      
      return data;
    } catch (error) {
      console.error('API请求错误:', error);
      throw error;
    }
  }

  // 商品相关API (从公开goods API获取，限定台湾标签)
  async getProducts(page = 0, size = 10): Promise<ProductListResponse> {
    // 从公开goods API获取，并过滤台湾标签的商品
    const response = await this.request<any>(`/goods/public?page=${page}&size=${size}&tags=台湾`);
    return {
      success: response.success || true,
      products: this.transformGoodsList(response.goods || []),
      totalElements: response.totalElements || 0,
      totalPages: response.totalPages || 0,
      currentPage: response.currentPage || page
    };
  }

  async getProduct(id: number): Promise<{ success: boolean; product: Product }> {
    const response = await this.request<any>(`/goods/public/${id}`);
    return {
      success: response.success || true,
      product: this.transformGoodsToProduct(response.goods || response)
    };
  }

  async searchProducts(keyword: string): Promise<{ success: boolean; products: Product[] }> {
    const response = await this.request<any>(`/goods/public?name=${encodeURIComponent(keyword)}&tags=台湾`);
    return {
      success: response.success || true,
      products: this.transformGoodsList(response.goods || [])
    };
  }

  async getProductsByPriceRange(minPrice: number, maxPrice: number): Promise<{ success: boolean; products: Product[] }> {
    // 从公开goods API获取并过滤台湾标签的商品
    const response = await this.request<any>(`/goods/public?tags=台湾`);
    let transformedProducts = this.transformGoodsList(response.goods || []);
    
    // 前端价格过滤
    const filteredProducts = transformedProducts.filter((product: Product) => {
      const price = product.price || 0;
      return price >= minPrice && price <= maxPrice;
    });
    
    return {
      success: response.success || true,
      products: filteredProducts
    };
  }

  async getProductCategories(): Promise<{ success: boolean; categories: Record<string, string> }> {
    // 从goods API获取分类信息
    const response = await this.request<any>('/goods?tags=台湾');
    const products = response.goods || [];
    
    // 提取唯一分类
    const categories: Record<string, string> = {};
    products.forEach((product: any) => {
      if (product.category && !categories[product.category]) {
        categories[product.category] = product.category;
      }
    });
    
    return {
      success: response.success || true,
      categories: categories
    };
  }

  // 订单相关API
  async createOrder(orderData: any): Promise<{ success: boolean; order: any; message?: string }> {
    return this.request<{ success: boolean; order: any; message?: string }>('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  }

  // 购物车相关API
  async getCart(): Promise<{ success: boolean; cart: any; message?: string }> {
    return this.request<{ success: boolean; cart: any; message?: string }>('/cart', {
      method: 'GET',
    });
  }

  async addToCart(goodsId: number, quantity: number = 1): Promise<{ success: boolean; cart: any; message?: string }> {
    return this.request<{ success: boolean; cart: any; message?: string }>('/cart/items', {
      method: 'POST',
      body: JSON.stringify({ goodsId, quantity }),
    });
  }

  async updateCartItemQuantity(goodsId: number, quantity: number): Promise<{ success: boolean; cart: any; message?: string }> {
    return this.request<{ success: boolean; cart: any; message?: string }>(`/cart/items/${goodsId}?quantity=${quantity}`, {
      method: 'PUT',
    });
  }

  async removeFromCart(goodsId: number): Promise<{ success: boolean; cart: any; message?: string }> {
    return this.request<{ success: boolean; cart: any; message?: string }>(`/cart/items/${goodsId}`, {
      method: 'DELETE',
    });
  }

  async clearCart(): Promise<{ success: boolean; cart: any; message?: string }> {
    return this.request<{ success: boolean; cart: any; message?: string }>('/cart', {
      method: 'DELETE',
    });
  }

  async getCartItemCount(): Promise<{ success: boolean; count: number; message?: string }> {
    return this.request<{ success: boolean; count: number; message?: string }>('/cart/count', {
      method: 'GET',
    });
  }

  async isItemInCart(goodsId: number): Promise<{ success: boolean; exists: boolean; message?: string }> {
    return this.request<{ success: boolean; exists: boolean; message?: string }>(`/cart/items/${goodsId}/exists`, {
      method: 'GET',
    });
  }

  async getOrder(orderId: number): Promise<{ success: boolean; order: Order }> {
    return this.request<{ success: boolean; order: Order }>(`/orders/${orderId}`);
  }

  async getOrderByNumber(orderNumber: string): Promise<{ success: boolean; order: Order }> {
    return this.request<{ success: boolean; order: Order }>(`/orders/number/${orderNumber}`);
  }

  async getUserOrders(page = 0, size = 10): Promise<OrderListResponse> {
    return this.request<OrderListResponse>(`/orders/user?page=${page}&size=${size}`);
  }

  async updateOrderStatus(orderId: number, status: string, notes?: string): Promise<{ success: boolean; order: Order }> {
    const params = new URLSearchParams({ status });
    if (notes) params.append('notes', notes);
    return this.request<{ success: boolean; order: Order }>(`/orders/${orderId}/status?${params}`, {
      method: 'PUT',
    });
  }

  async cancelOrder(orderId: number, reason?: string): Promise<{ success: boolean; order: Order }> {
    const params = new URLSearchParams();
    if (reason) params.append('reason', reason);
    return this.request<{ success: boolean; order: Order }>(`/orders/${orderId}/cancel?${params}`, {
      method: 'POST',
    });
  }

  // 配送相关API
  async getDeliveryByOrderId(orderId: number): Promise<Delivery> {
    return this.request<Delivery>(`/deliveries/order/${orderId}`);
  }

  async getDeliveryByTrackingNumber(trackingNumber: string): Promise<Delivery> {
    return this.request<Delivery>(`/deliveries/tracking/${trackingNumber}`);
  }

  async getUserDeliveries(userId: number): Promise<Delivery[]> {
    return this.request<Delivery[]>(`/deliveries/user/${userId}`);
  }

  // 客服相关API
  async createConversation(request: CreateConversationRequest): Promise<SupportConversation> {
    return this.request<SupportConversation>('/support/conversations', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getUserConversations(userId: number): Promise<SupportConversation[]> {
    return this.request<SupportConversation[]>(`/support/users/${userId}/conversations`);
  }

  async getConversationMessages(conversationId: number): Promise<SupportMessage[]> {
    return this.request<SupportMessage[]>(`/support/conversations/${conversationId}/messages`);
  }

  async sendMessage(conversationId: number, senderId: number, senderType: 'USER' | 'ADMIN', message: string): Promise<SupportMessage> {
    return this.request<SupportMessage>(`/support/conversations/${conversationId}/messages?senderId=${senderId}&senderType=${senderType}`, {
      method: 'POST',
      body: JSON.stringify(message),
    });
  }

  // 支付相关API
  async createPayment(paymentData: PaymentRequest): Promise<{ success: boolean; paymentUrl?: string }> {
    return this.request<{ success: boolean; paymentUrl?: string }>('/payments', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async queryPaymentStatus(orderId: number): Promise<{ success: boolean; status: string }> {
    return this.request<{ success: boolean; status: string }>(`/payments/order/${orderId}`);
  }

  async getOrderShippingInfo(orderId: number): Promise<{ success: boolean; data: any[] }> {
    return this.request<{ success: boolean; data: any[] }>(`/orders/${orderId}/shipping`);
  }

  async getAvailableCoupons(): Promise<{ success: boolean; data: any[] }> {
    return this.request<{ success: boolean; data: any[] }>('/coupons/available');
  }

  // 销户相关API
  async sendDeleteVerificationCode(email: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/auth/send-delete-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async deleteAccount(email: string, code: string): Promise<{ success: boolean; message: string }> {
    return this.request<{ success: boolean; message: string }>('/auth/delete-account', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
  }

  // 获取用户收货地址
  async getShippingAddress(): Promise<{ success: boolean; shippingAddress?: string; contactPhone?: string }> {
    try {
      const response = await this.request<{ shippingAddress?: string; contactPhone?: string }>('/auth/shipping-address');
      return {
        success: true,
        shippingAddress: response.shippingAddress,
        contactPhone: response.contactPhone
      };
    } catch (error) {
      return {
        success: false,
        shippingAddress: undefined,
        contactPhone: undefined
      };
    }
  }

  // 更新用户收货地址
  async updateShippingAddress(shippingAddress: string, contactPhone: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.request<{ message: string }>('/auth/shipping-address', {
        method: 'PUT',
        body: JSON.stringify({ shippingAddress, contactPhone }),
      });
      return {
        success: true,
        message: response.message || '更新成功'
      };
    } catch (error) {
      return {
        success: false,
        message: '更新失败'
      };
    }
  }

  // 社交媒体分享相关API
  async createSocialShare(orderId: number, platform: string, shareUrl: string, description?: string): Promise<{ success: boolean; message: string; data?: any }> {
    return this.request<any>('/social-shares', {
      method: 'POST',
      body: JSON.stringify({ orderId, platform, shareUrl, description }),
    });
  }

  async getMySocialShares(): Promise<{ success: boolean; data: any[] }> {
    try {
      const response = await this.request<any[]>('/social-shares/my-shares');
      return {
        success: true,
        data: response
      };
    } catch (error) {
      return {
        success: false,
        data: []
      };
    }
  }

  async getOrderSocialShares(orderId: number): Promise<{ success: boolean; data: any[] }> {
    try {
      const response = await this.request<any[]>(`/social-shares/order/${orderId}`);
      return {
        success: true,
        data: response
      };
    } catch (error) {
      return {
        success: false,
        data: []
      };
    }
  }

  // 商品评价相关API
  async createProductReview(goodsId: number, userId: number, rating: number, content: string, isAnonymous?: boolean): Promise<{ success?: boolean; message?: string; data?: any; isUpdate?: boolean; review?: any }> {
    return this.request<any>('/goods-reviews', {
      method: 'POST',
      body: JSON.stringify({ goodsId, userId, rating, content, isAnonymous }),
    });
  }

  async getProductReviews(goodsId: number, page: number = 0, size: number = 10): Promise<{ success: boolean; data: any[] }> {
    try {
      const response = await this.request<any>(`/goods-reviews?goodsId=${goodsId}&page=${page}&size=${size}&status=APPROVED`);
      return {
        success: true,
        data: response.reviews || []
      };
    } catch (error) {
      console.error('获取商品评价失败:', error);
      return {
        success: false,
        data: []
      };
    }
  }

  // 获取用户对特定商品的评价
  async getUserGoodsReview(goodsId: number, userId: number): Promise<{ success: boolean; data: any | null }> {
    try {
      const response = await this.request<any>(`/goods-reviews?goodsId=${goodsId}&userId=${userId}&page=0&size=1`);
      if (response.reviews && response.reviews.length > 0) {
        return {
          success: true,
          data: response.reviews[0]
        };
      }
      return {
        success: true,
        data: null
      };
    } catch (error) {
      console.error('获取用户评价失败:', error);
      return {
        success: false,
        data: null
      };
    }
  }

  async getProductReviewStats(goodsId: number): Promise<{ success: boolean; data: any }> {
    try {
      const response = await this.request<any>(`/goods-reviews/goods/${goodsId}/statistics`);
      return {
        success: true,
        data: response
      };
    } catch (error) {
      console.error('获取评价统计失败:', error);
      return {
        success: false,
        data: {}
      };
    }
  }

  async getMyReviews(): Promise<{ success: boolean; data: any[] }> {
    try {
      const response = await this.request<any[]>('/reviews/my-reviews');
      return {
        success: true,
        data: response
      };
    } catch (error) {
      return {
        success: false,
        data: []
      };
    }
  }

  async canReviewProduct(productId: number): Promise<{ success: boolean; canReview: boolean }> {
    try {
      const response = await this.request<{ canReview: boolean }>(`/reviews/can-review/${productId}`);
      return {
        success: true,
        canReview: response.canReview
      };
    } catch (error) {
      return {
        success: false,
        canReview: false
      };
    }
  }

  async markReviewAsHelpful(reviewId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.request<{ message: string }>(`/reviews/${reviewId}/helpful`, {
        method: 'POST',
      });
      return {
        success: true,
        message: response.message || '感谢您的反馈'
      };
    } catch (error) {
      return {
        success: false,
        message: '操作失败'
      };
    }
  }

  // 优惠券阅读相关API
  async getUnreadCoupons(): Promise<{ success: boolean; data: any[]; count: number }> {
    try {
      const response = await this.request<{ data: any[]; count: number }>('/coupons/unread');
      return {
        success: true,
        data: response.data || [],
        count: response.count || 0
      };
    } catch (error) {
      return {
        success: false,
        data: [],
        count: 0
      };
    }
  }

  async markCouponAsRead(couponId: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.request<{ message: string }>(`/coupons/${couponId}/mark-read`, {
        method: 'POST',
      });
      return {
        success: true,
        message: response.message || '已标记为已读'
      };
    } catch (error) {
      return {
        success: false,
        message: '操作失败'
      };
    }
  }

  // 聊天和留言相关API
  async sendChatMessage(content: string): Promise<{ success: boolean; message?: string; data?: any }> {
    return this.request<any>('/chat/send', {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async sendStrangerMessage(data: { name: string; email: string; phone?: string; subject: string; message: string }): Promise<{ success: boolean; message?: string; data?: any }> {
    return this.request<any>('/stranger-messages', {
      method: 'POST',
      body: JSON.stringify({ ...data, status: 'PENDING' }),
    });
  }

  // 优惠券管理相关API
  async getCouponTotal(): Promise<{ success: boolean; data?: any }> {
    const response = await this.request<any>('/coupons/total');
    return {
      success: response.success || true,
      data: response.data || response
    };
  }

  async getCouponList(): Promise<{ success: boolean; data?: any[] }> {
    const response = await this.request<any>('/coupons/list');
    return {
      success: response.success || true,
      data: response.data || response.coupons || []
    };
  }

  async getExpiringCoupons(): Promise<{ success: boolean; data?: any[] }> {
    const response = await this.request<any>('/coupons/expiring');
    return {
      success: response.success || true,
      data: response.data || response.coupons || []
    };
  }
}

export const apiService = new ApiService();
